import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  GoogleAddressAutocomplete,
  type ParsedAddress,
} from '@/components/molecules/GoogleAddressAutocomplete';
import { COUNTRY_OPTIONS, getRegionOptions, type RegionOption } from '@/lib/geo/regions';

/** The country select offers only these, so Places suggests only these. */
const PLACES_COUNTRIES = ['us', 'ca'];

export interface AddressFieldsValue {
  street1: string;
  street2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

/**
 * Shared US/CA address fields: Google autocomplete on Street 1 (autofills the
 * rest), Street 2, a proportioned City / State (Province for Canada) / Postal
 * row, and a Country select. Used by the contact address form and the bill-to
 * override address — a single source of truth so they never diverge.
 *
 * `onChange` is a patch: it receives only the field(s) that changed.
 */
export function AddressFields({
  value,
  onChange,
  idPrefix = 'address',
  disabled = false,
  unsetCountryIsBlank = false,
}: {
  value: AddressFieldsValue;
  onChange: (patch: Partial<AddressFieldsValue>) => void;
  idPrefix?: string;
  /** Read-only display: every field, the Places street box included, stops taking input. */
  disabled?: boolean;
  /**
   * Show an empty country as unset instead of as the United States — for
   * values whose country is genuinely unknown, which must not read as US.
   */
  unsetCountryIsBlank?: boolean;
}) {
  // A value from before these were selects (a typed "Texas") or from a Places
  // pick outside the list stays visible as its own option, rather than the
  // select going blank over data that is really there.
  const regionOptions = withCurrent(getRegionOptions(value.country), value.state);
  const countryOptions = withCurrent(COUNTRY_OPTIONS, value.country);
  const regionLabel = value.country === 'CA' ? 'Province' : 'State';

  function handleGoogleAddress(parsed: ParsedAddress) {
    onChange({
      street1: parsed.street,
      city: parsed.city,
      state: parsed.state,
      postalCode: parsed.zip,
      country: parsed.country || 'US',
    });
  }

  return (
    <div className="space-y-4">
      {disabled ? (
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-street1`}>Street 1</Label>
          <Input id={`${idPrefix}-street1`} value={value.street1} disabled />
        </div>
      ) : (
        <GoogleAddressAutocomplete
          id={`${idPrefix}-street1`}
          label="Street 1"
          placeholder="123 Main St"
          value={value.street1}
          onChange={(next) => onChange({ street1: next })}
          onAddressSelect={handleGoogleAddress}
          countries={PLACES_COUNTRIES}
        />
      )}

      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-street2`}>Street 2</Label>
        <Input
          id={`${idPrefix}-street2`}
          value={value.street2}
          disabled={disabled}
          onChange={(event) => onChange({ street2: event.target.value })}
          placeholder="Suite, apt, etc."
        />
      </div>

      <div className="flex flex-wrap gap-3 sm:flex-nowrap">
        <div className="min-w-0 flex-1 space-y-1.5">
          <Label htmlFor={`${idPrefix}-city`}>City</Label>
          <Input
            id={`${idPrefix}-city`}
            value={value.city}
            disabled={disabled}
            onChange={(event) => onChange({ city: event.target.value })}
          />
        </div>
        <div className="w-full space-y-1.5 sm:w-28">
          <Label htmlFor={`${idPrefix}-state`}>{regionLabel}</Label>
          <Select
            value={value.state}
            disabled={disabled}
            onValueChange={(next) => onChange({ state: next })}
          >
            <SelectTrigger id={`${idPrefix}-state`} className="w-full">
              <SelectValue placeholder={regionLabel} />
            </SelectTrigger>
            <SelectContent>
              {regionOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full space-y-1.5 sm:w-36">
          <Label htmlFor={`${idPrefix}-postal-code`}>Postal Code</Label>
          <Input
            id={`${idPrefix}-postal-code`}
            value={value.postalCode}
            disabled={disabled}
            onChange={(event) => onChange({ postalCode: event.target.value })}
          />
        </div>
      </div>

      <div className="space-y-1.5 sm:w-52">
        <Label htmlFor={`${idPrefix}-country`}>Country</Label>
        <Select
          value={value.country || (unsetCountryIsBlank ? '' : 'US')}
          disabled={disabled}
          onValueChange={(next) => onChange({ country: next, state: '' })}
        >
          <SelectTrigger id={`${idPrefix}-country`} className="w-full">
            <SelectValue placeholder="Country" />
          </SelectTrigger>
          <SelectContent>
            {countryOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function withCurrent(options: RegionOption[], current: string): RegionOption[] {
  return current && !options.some((option) => option.value === current)
    ? [...options, { value: current, label: current }]
    : options;
}
